import { Button, Grid, Typography } from "@mui/material";
import { useState } from "react";
import CalendarTabView from "./CalendarTabView";
import { useCalendarTab } from "./useCalendarTab";

const THIS_YEAR = new Date().getFullYear();

const LegendDot = ({ color, dashed }: { color: string; dashed?: boolean }) => (
  <span
    style={{
      width: 12,
      height: 12,
      borderRadius: "50%",
      background: color,
      display: "inline-block",
      flexShrink: 0,
      ...(dashed
        ? {
            outline: `2px dashed ${color === "#7b9cff" ? "#4a6ef5" : "#999"}`,
            outlineOffset: 2,
          }
        : {}),
    }}
  />
);

const LEGEND = [
  { color: "#075e5b", label: "Generated" },
  { color: "#f6ae2d", label: "Company specific" },
  { color: "#7b9cff", label: "Unsaved", dashed: true },
  { color: "#ccc", label: "Pending removal", dashed: true },
] as const;

const CalendarUI = () => {
  const [year, setYear] = useState(THIS_YEAR);
  const calendarTab = useCalendarTab(year);

  return (
    <Grid container>
      <Grid item xs={12}>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            padding: "0.5rem 0",
            justifyContent: "space-between",
            alignItems: "center",
            background: "var(--header)",
            color: "#fff",
            minHeight: "6vh",
          }}
        >
          <div style={{ paddingLeft: "1rem" }}>
            <Typography
              fontWeight="bold"
              variant="h6"
              style={{ textTransform: "uppercase", color: "#fff" }}
            >
              Calendar
            </Typography>
          </div>

          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              alignItems: "center",
              margin: "0 1rem",
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: 6,
                padding: "0.4rem 0.75rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.2rem",
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                color="text.primary"
              >
                Production closed days — {year}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Click a day to mark or unmark it as closed. Save to persist
                changes.
              </Typography>
              <div
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                {LEGEND.map(({ color, label, dashed }) => (
                  <span
                    key={label}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                      fontSize: "0.8rem",
                      color: "black",
                    }}
                  >
                    <LegendDot color={color} dashed={dashed} />
                    {label}
                  </span>
                ))}
              </div>
            </div>

            <Button
              variant="contained"
              onClick={calendarTab.save}
              disabled={calendarTab.saving || !calendarTab.hasChanges}
              sx={{
                minWidth: "12em",
                backgroundColor: calendarTab.saving
                  ? "grey"
                  : "var(--buttonYellow)",
                "&:hover": {
                  backgroundColor: calendarTab.saving
                    ? "grey"
                    : "var(--buttonYellowHover)",
                },
              }}
            >
              {calendarTab.saving ? "Saving..." : "Save Calendar"}
            </Button>
          </div>
        </div>
      </Grid>

      <Grid item xs={12}>
        <CalendarTabView
          year={year}
          onYearChange={setYear}
          dayMap={calendarTab.dayMap}
          localChanges={calendarTab.localChanges}
          pendingNames={calendarTab.pendingNames}
          onTileClick={calendarTab.onTileClick}
          pendingRange={calendarTab.pendingRange}
          nameDialogState={calendarTab.nameDialogState}
          confirmNameDialog={calendarTab.confirmNameDialog}
          cancelNameDialog={calendarTab.cancelNameDialog}
        />
      </Grid>
    </Grid>
  );
};

export default CalendarUI;
